import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../api/client';

export type StartupCheckStatus = 'ok' | 'warning' | 'error';

export interface StartupCheck {
  key: 'api' | 'health_full' | 'admin_api' | 'celery';
  title: string;
  status: StartupCheckStatus;
  details: string;
}

interface FullHealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  checks?: Record<string, { status?: string }>;
}

export function useStartupChecks() {
  const [checks, setChecks] = useState<StartupCheck[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);

  const runChecks = useCallback(async () => {
    setIsLoading(true);

    const nextChecks: StartupCheck[] = [];

    try {
      const apiResponse = await apiClient.get<{ status?: string; version?: string }>('/health');
      nextChecks.push({
        key: 'api',
        title: 'Backend API',
        status: apiResponse.data?.status === 'healthy' ? 'ok' : 'warning',
        details: apiResponse.data?.version
          ? `Health endpoint responded (version ${apiResponse.data.version})`
          : 'Health endpoint responded',
      });
    } catch (error) {
      nextChecks.push({
        key: 'api',
        title: 'Backend API',
        status: 'error',
        details: error instanceof Error ? error.message : 'Health endpoint is unreachable',
      });
    }

    try {
      const healthResponse = await apiClient.get<FullHealthResponse>('/health/full');
      const overall = healthResponse.data?.status ?? 'unhealthy';
      const dependencySummary = healthResponse.data?.checks
        ? Object.entries(healthResponse.data.checks)
            .map(([name, item]) => `${name}:${item.status ?? 'unknown'}`)
            .join(', ')
        : 'no dependency details';

      nextChecks.push({
        key: 'health_full',
        title: 'Dependencies (DB/Redis)',
        status: overall === 'healthy' ? 'ok' : overall === 'degraded' ? 'warning' : 'error',
        details: `Overall ${overall}. ${dependencySummary}`,
      });
    } catch (error) {
      nextChecks.push({
        key: 'health_full',
        title: 'Dependencies (DB/Redis)',
        status: 'error',
        details: error instanceof Error ? error.message : 'Full health check failed',
      });
    }

    try {
      await apiClient.get('/admin/dashboard');
      nextChecks.push({
        key: 'admin_api',
        title: 'Admin endpoints',
        status: 'ok',
        details: 'Admin dashboard endpoint is accessible',
      });
    } catch (error) {
      nextChecks.push({
        key: 'admin_api',
        title: 'Admin endpoints',
        status: 'error',
        details: error instanceof Error ? error.message : 'Admin API is not accessible',
      });
    }

    // Scheduler runs in-process (no Celery). If API+DB+Redis are OK, scheduler is OK.
    const apiOk = nextChecks.find(c => c.key === 'api')?.status === 'ok';
    const depsOk = nextChecks.find(c => c.key === 'health_full')?.status === 'ok';
    nextChecks.push({
      key: 'celery',
      title: 'Task Scheduler',
      status: apiOk && depsOk ? 'ok' : 'warning',
      details: apiOk && depsOk
        ? 'In-process scheduler running (email polling, health checks, reminders)'
        : 'Scheduler depends on API and DB/Redis being healthy',
    });

    setChecks(nextChecks);
    setLastCheckedAt(new Date().toISOString());
    setIsLoading(false);
  }, []);

  useEffect(() => {
    void runChecks();
    const timer = window.setInterval(() => {
      void runChecks();
    }, 60000);

    return () => window.clearInterval(timer);
  }, [runChecks]);

  const overallStatus = useMemo<StartupCheckStatus>(() => {
    if (checks.some((item) => item.status === 'error')) {
      return 'error';
    }
    if (checks.some((item) => item.status === 'warning')) {
      return 'warning';
    }
    return 'ok';
  }, [checks]);

  return {
    checks,
    overallStatus,
    isLoading,
    lastCheckedAt,
    recheck: runChecks,
  };
}

